import {
  Blocker,
  Schedule,
  OneTimeSlot,
  TimeSlot,
  useBlockerMutation,
  useBlocker,
  useSchedule,
  useScheduleMutation,
  useOneTimeSlots,
  useOneTimeSlotMutation,
} from "../../api/scheduleApi.ts";
import { useEffect, useMemo, useState } from "react";
import BlockerSettings from "../../components/schedule/BlockerSettings.tsx";
import OneTimeSlotSettings from "../../components/schedule/OneTimeSlotsSettings.tsx";
import TimeslotSettings from "../../components/schedule/TimeslotSettings.tsx";
import { LocationWithName, useLocationsQuery } from "../../api/locationApi.ts";
import LinkButton from "../../components/LinkButton.tsx";
import { useParams } from "react-router";
import { Trans } from "@lingui/react/macro";
import Container from "../../components/Container.tsx";
import ContainerHeader from "../../components/ContainerHeader.tsx";
import { Tabs } from "../../components/Tabs.tsx";

export const ScheduleSettings = () => {
  const [isAdding, setIsAdding] = useState(false);
  const [deletingScheduleId, setDeletingScheduleId] = useState<string | null>(
    null,
  );
  const [deletingOneTimeSlot, setDeletingOneTimeSlot] = useState<string | null>(
    null,
  );
  const [deletingBlockerId, setDeletingBlockerId] = useState<string | null>(
    null,
  );

  const { data: schedules, refetch: refetchSchedule } = useSchedule();
  const { mutate: scheduleUpdateMutation } = useScheduleMutation();
  const { data: oneTimeSlots, refetch: refetchOneTimeSlots } =
    useOneTimeSlots();
  const { mutate: oneTimeSlotUpdateMutation } = useOneTimeSlotMutation();
  const { data: blockers, refetch: refetchBlockers } = useBlocker();
  const { mutate: blockerMutation } = useBlockerMutation();
  const { data: locations } = useLocationsQuery();
  const [selectedLocation, setSelectedLocation] =
    useState<LocationWithName | null>(null);

  useEffect(() => {
    if (locations && locations.length > 0) {
      setSelectedLocation(locations[0]);
    }
  }, [locations]);

  const timeslots: TimeSlot[] = useMemo(() => {
    const allTimeSlots = schedules?.flatMap(({ timeSlots }) => timeSlots);
    return allTimeSlots ? allTimeSlots : [];
  }, [schedules]);

  const oneTimeSlotsArray = useMemo(() => {
    return oneTimeSlots?.oneTimeSlots ? oneTimeSlots.oneTimeSlots : [];
  }, [oneTimeSlots]);

  const blockerArray = useMemo(() => {
    return blockers?.blockers ? blockers.blockers : [];
  }, [blockers]);

  const handleAddTimeSlot = (newTimeSlot: TimeSlot) => {
    setIsAdding(true);
    const schedule = schedules?.find(
      ({ locationId }) => locationId === newTimeSlot.locationId,
    );
    const updatedSchedule: Schedule = {
      id: schedule?.id,
      locationId: newTimeSlot.locationId,
      timeSlots: [...(schedule?.timeSlots || []), newTimeSlot],
    };
    scheduleUpdateMutation(updatedSchedule, {
      onSettled: () => {
        refetchSchedule()
          .catch(console.error)
          .finally(() => setIsAdding(false));
      },
    });
  };

  const handleDeleteTimeSlot = (updateLocationId: string, id: string) => {
    setDeletingScheduleId(id);
    const schedule = schedules?.find(
      ({ locationId }) => locationId === updateLocationId,
    );
    const timeSlots =
      schedule?.timeSlots.filter((timeSlot) => timeSlot.id !== id) || [];
    scheduleUpdateMutation(
      {
        id: schedule!.id,
        locationId: schedule!.locationId,
        timeSlots: timeSlots,
      },
      {
        onSettled: () => {
          refetchSchedule()
            .catch(console.error)
            .finally(() => setDeletingScheduleId(null));
        },
      },
    );
  };

  const handleAddOneTimeSlot = (newOneTimeSlot: OneTimeSlot) => {
    setIsAdding(true);
    oneTimeSlotUpdateMutation(newOneTimeSlot, {
      onSettled: () => {
        refetchOneTimeSlots()
          .catch(console.error)
          .finally(() => setIsAdding(false));
      },
    });
  };

  const handleDeleteOneTimeSlot = (id: string) => {
    setDeletingOneTimeSlot(id);
    const oneTimeSlotToDelete = oneTimeSlotsArray.find(
      (value) => value.id === id,
    );
    if (!oneTimeSlotToDelete) return;

    oneTimeSlotUpdateMutation(oneTimeSlotToDelete, {
      onSettled: () => {
        refetchOneTimeSlots()
          .catch(console.error)
          .finally(() => setDeletingOneTimeSlot(null));
      },
    });
  };

  const handleAddBlocker = (newBlocker: Blocker) => {
    setIsAdding(true);
    blockerMutation(newBlocker, {
      onSettled: () => {
        refetchBlockers()
          .catch(console.error)
          .finally(() => setIsAdding(false));
      },
    });
  };

  const handleDeleteBlocker = (id: string) => {
    setDeletingBlockerId(id);
    const blockerToDelete = blockerArray.find((value) => value.id === id);
    if (!blockerToDelete) return;

    blockerMutation(blockerToDelete, {
      onSettled: () => {
        refetchBlockers()
          .catch(console.error)
          .finally(() => setDeletingBlockerId(null));
      },
    });
  };

  return (
    <Container className={"mx-auto w-full max-w-3xl"}>
      <ContainerHeader className={"flex"}>
        <Trans>Your teaching schedules</Trans>
      </ContainerHeader>

      {locations && locations.length === 0 ? (
        <NoLocationPlaceholder />
      ) : (
        <Tabs defaultValue="schedule">
          <Tabs.Tab label={<Trans>Schedule</Trans>} value="schedule">
            <TimeslotSettings
              onAddTimeslot={handleAddTimeSlot}
              existingTimeslots={timeslots}
              isLoading={isAdding}
              onDeleteTimeslot={handleDeleteTimeSlot}
              deletingId={deletingScheduleId}
              onSelectLocation={setSelectedLocation}
              selectedLocation={selectedLocation!}
              locations={locations || []}
            />
          </Tabs.Tab>

          <Tabs.Tab
            label={<Trans>One-Time Teaching Slots</Trans>}
            value="one-time-slots"
          >
            <OneTimeSlotSettings
              onAddOneTimeSlot={handleAddOneTimeSlot}
              existingOneTimeSlots={oneTimeSlotsArray}
              isLoading={isAdding}
              onDeleteOneTimeSlot={handleDeleteOneTimeSlot}
              deletingId={deletingOneTimeSlot}
              onSelectLocation={setSelectedLocation}
              selectedLocation={selectedLocation!}
              locations={locations || []}
            />
          </Tabs.Tab>

          <Tabs.Tab label={<Trans>Blocker</Trans>} value="blocker">
            <BlockerSettings
              onAddBlocker={handleAddBlocker}
              existingBlocker={blockerArray}
              isLoading={isAdding}
              onDeleteBlocker={handleDeleteBlocker}
              deletingBlockerId={deletingBlockerId}
            />
          </Tabs.Tab>
        </Tabs>
      )}
    </Container>
  );
};

const NoLocationPlaceholder = () => {
  const { userId } = useParams();

  return (
    <>
      <p className={"mb-4"}>
        You need to add a location before you can create a schedule.
      </p>
      <LinkButton to={`/profile/${userId}/edit/locations`}>
        Locations
      </LinkButton>
    </>
  );
};
